import CardFillDetailDialog from '@/components/client/card-fill-detail-dialog';
import CardProviderIcon from '@/components/card-provider-icon';
import CardTileBalance from '@/components/client/card-tile-balance';
import { Button } from '@/components/ui/button';
import type { ICard, ICardFillJob, IPhone } from '@/entities/client.entity';
import { hasActiveBalanceFetchJob, hasActiveCardJob, isCardBalanceFetchReady } from '@/lib/card-fill-meta';
import { requestCardBalance } from '@/lib/card-fill-actions';
import { formatCardNumber, resolveCardDisplayNumber, resolveCardProvider } from '@/lib/format-card';
import { cn } from '@/lib/utils';
import { InfoIcon, Loader2Icon, RefreshCwIcon } from 'lucide-react';
import { useState, type MouseEvent } from 'react';

interface ClientCardTileProps {
	card: ICard;
	phones?: IPhone[];
	cardFillJobs?: ICardFillJob[];
	onCopy: (text: string) => void;
}

export default function ClientCardTile({ card, phones, cardFillJobs, onCopy }: ClientCardTileProps) {
	const [detailOpen, setDetailOpen] = useState(false);
	const provider = resolveCardProvider(card);
	const displayNumber = formatCardNumber(card);
	const hasBalance = card.balance != null;
	const hasDate = Boolean(card.date?.trim());
	const isActive = hasActiveCardJob(cardFillJobs, card.id);
	const balanceRefreshing = hasActiveBalanceFetchJob(cardFillJobs, card.id);
	const canRefreshBalance = isCardBalanceFetchReady(card);
	const copyValue = resolveCardDisplayNumber(card);

	function handleNumberCopy(e: MouseEvent) {
		e.stopPropagation();
		if (copyValue !== '—') onCopy(copyValue);
	}

	return (
		<>
			<div
				className={cn(
					'rounded-lg border border-border bg-card p-2 shadow-sm',
					!hasBalance && 'opacity-95',
				)}
			>
				<div className='flex items-center gap-2 min-w-0'>
					<CardProviderIcon provider={provider} />
					<button
						type='button'
						className='min-w-0 flex-1 text-sm font-semibold font-mono break-all text-left hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 -ml-0.5 cursor-pointer select-none'
						onClick={handleNumberCopy}
					>
						{displayNumber}
					</button>
					{!hasDate && hasBalance ? (
						<CardTileBalance balance={card.balance ?? 0} onCopy={onCopy} className='shrink-0' />
					) : null}
					<div className='flex shrink-0 items-center gap-0.5'>
						{isActive ? <Loader2Icon size={14} className='animate-spin text-chart-3' /> : null}
						{canRefreshBalance ? (
							<Button
								type='button'
								variant='ghost'
								size='sm'
								className='h-7 w-7 p-0'
								disabled={balanceRefreshing}
								onClick={e => {
									e.stopPropagation();
									requestCardBalance(card.id);
								}}
							>
								{balanceRefreshing ? (
									<Loader2Icon size={14} className='animate-spin' />
								) : (
									<RefreshCwIcon size={14} />
								)}
							</Button>
						) : null}
						<Button
							type='button'
							variant='ghost'
							size='sm'
							className='h-7 w-7 p-0'
							onClick={e => {
								e.stopPropagation();
								setDetailOpen(true);
							}}
						>
							<InfoIcon size={14} />
						</Button>
					</div>
				</div>

				{hasDate ? (
					<div className='mt-1 flex items-baseline justify-between gap-2 min-w-0 pl-7'>
						<span className='text-xs text-muted-foreground tabular-nums truncate'>{card.date}</span>
						{hasBalance ? (
							<CardTileBalance balance={card.balance ?? 0} onCopy={onCopy} />
						) : null}
					</div>
				) : null}
			</div>

			<CardFillDetailDialog
				target={{ mode: 'card', card, phones }}
				jobs={cardFillJobs}
				open={detailOpen}
				onOpenChange={setDetailOpen}
			/>
		</>
	);
}
